/**
 * Track map rendering utilities and color modes.
 */

export interface CanvasPoint {
  x: number;
  y: number;
}

// ── Value → colour mapping ────────────────────────────────────────────

export type TrackColorMode = 'speed' | 'acceleration' | 'rpm' | 'gforce';

export function valueToColor(value: number, minVal: number, maxVal: number): string {
  const range = maxVal - minVal || 1;
  const t = (value - minVal) / range;
  let r: number, g: number, b: number;
  if (t < 0.5) {
    const u = t * 2;
    r = 255; g = Math.round(200 * u); b = 50;
  } else {
    const u = (t - 0.5) * 2;
    r = Math.round(255 * (1 - u)); g = Math.round(200 + 55 * u); b = Math.round(50 + 100 * u);
  }
  return `rgb(${r},${g},${b})`;
}

/** Divergent colour: red (negative) → yellow (zero) → green (positive) */
export function divergentValueToColor(value: number, absMax: number): string {
  const bound = absMax || 1;
  // Clamp to [-bound, bound], then map to [0, 1]
  const clamped = Math.max(-bound, Math.min(bound, value));
  const t = (clamped + bound) / (2 * bound); // 0 = most negative, 0.5 = zero, 1 = most positive
  let r: number, g: number, b: number;
  if (t < 0.5) {
    const u = t * 2;
    r = 255; g = Math.round(200 * u); b = 50;
  } else {
    const u = (t - 0.5) * 2;
    r = Math.round(255 * (1 - u)); g = Math.round(200 + 55 * u); b = Math.round(50 + 100 * u);
  }
  return `rgb(${r},${g},${b})`;
}

// ── Value legend ──────────────────────────────────────────────────────

export function drawLegend(
  ctx: CanvasRenderingContext2D,
  size: number,
  minVal: number,
  maxVal: number,
  unit: string,
  divergent: boolean = false,
  inverted: boolean = false
) {
  const lw = 120, lh = 6;
  const lx = size - lw - 20;
  const ly = size - 20;
  const grad = ctx.createLinearGradient(lx, 0, lx + lw, 0);
  if (inverted) {
    grad.addColorStop(0, 'rgb(0, 255, 150)');
    grad.addColorStop(0.5, 'rgb(255, 200, 50)');
    grad.addColorStop(1, 'rgb(255, 0, 50)');
  } else {
    grad.addColorStop(0, 'rgb(255, 0, 50)');
    grad.addColorStop(0.5, 'rgb(255, 200, 50)');
    grad.addColorStop(1, 'rgb(0, 255, 150)');
  }
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.roundRect(lx, ly, lw, lh, 3);
  ctx.fill();
  ctx.font = '500 9px Inter, sans-serif';
  ctx.fillStyle = '#55556a';
  const decimals = (unit === 'G' || unit === 'm/s²') ? 1 : 0;
  ctx.textAlign = 'left';
  ctx.fillText(minVal.toFixed(decimals), lx, ly - 4);
  ctx.textAlign = 'right';
  ctx.fillText(maxVal.toFixed(decimals) + (unit ? ' ' + unit : ''), lx + lw, ly - 4);
  if (divergent) {
    ctx.textAlign = 'center';
    ctx.fillText('0', lx + lw / 2, ly - 4);
  }
}

// ── Empty state ───────────────────────────────────────────────────────

export function drawEmptyState(canvas: HTMLCanvasElement, message: string) {
  const wrap = canvas.parentElement!;
  const size = Math.min(wrap.clientWidth, wrap.clientHeight) || 380;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = size * dpr;
  canvas.height = size * dpr;
  canvas.style.width = size + 'px';
  canvas.style.height = size + 'px';
  const ctx = canvas.getContext('2d')!;
  ctx.scale(dpr, dpr);
  ctx.fillStyle = '#0a0a0f';
  ctx.fillRect(0, 0, size, size);
  ctx.font = '500 13px Inter, sans-serif';
  ctx.fillStyle = '#55556a';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(message, size / 2, size / 2);
}

// ── Re-exports from lapDiffMath for backward compatibility ────────────

export {
  LAP_COLORS,
  type LapDiffMetric,
  type LapDiffStats,
  type LapDiffHoverInfo,
  type LapDiffTimeDiff,
} from '@/lib/map/lapDiffMath';
